import React, { useCallback, useEffect, useState, useRef } from "react";
import { IEventSummary, IShow } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import moment from 'moment-timezone';

import { apiService } from "src/services/api.service";
import { ShowTypes } from "@eagerdog/constants";

import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Button from "src/components/Button/Button";

import { type ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { type IResultColumnsType, getColumns } from "src/utils/ScoresGrid/getColumns";
import { getDataRows } from "src/utils/ScoresGrid/getDataRows";

import styles from "./ResultsViewer.module.scss";

interface IProps {
  event: IEventSummary;
}

// ShowTypes values are either a plain level list or an { levels, sections } config.
const getElementLevels = (value: any): string[] =>
  Array.isArray(value) ? value : (value?.levels ?? []);

const ResultsViewer: React.FC<IProps> = ({ event }) => {
  const [loaded, setLoaded] = useState<boolean>(false);

  const [show, setShow] = useState<IOption>({ value: "All Shows", id: "None" });
  const [showElement, setShowElement] = useState<IOption>({ value: "All", id: "None" });
  const [showLevel, setShowLevel] = useState<IOption>({ value: "All", id: "None" });

  const [rowData, setRowData] = useState<any[]>([]);

  const [showOptions, setShowOptions] = useState<IOption[]>([]);
  const [shows, setShows] = useState<IShow[]>([]);
  const showsRef = useRef<IShow[]>([]);
  
  useEffect(() => {
    showsRef.current = shows;
  }, [shows]);
  
  const [elementOptions, setElementOptions] = useState<IOption[]>([]);
  const [levelOptions, setLevelOptions] = useState<IOption[]>([]);

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto"
  });

  const getResults = useCallback((id: string) => {
    let query: any = {
      sort: [{
        attribute_name: "show_type",
        sort: "asc"
      }, {
        attribute_name: "created_at",
        sort: "asc"
      }],
    };

    return apiService.getEventScoresReadOnly(id, query).then((response) => {
      setRowData(getDataRows(response, event, []));
      return response;
    }).catch((error) => {
      console.error('Failed to load event scores:', error);
      // Error is already handled by parent Event.tsx component
      // Just silently fail here to avoid duplicate toasts
    });
  }, [event]);

  // Get columns with isUserView=true to hide admin columns
  let colDefs: ColDef<IResultColumnsType>[] = getColumns(show, event, shows, getResults, [], true);

  const defaultColDef: ColDef = {
    flex: 1
  };

  const getShows = (clubId: string, eventId: string) => {
    apiService.getClubEventShows(clubId, eventId).then((response) => {
      setShows(response);

      let options: IOption[] = [{ value: "All Shows", id: "None" }];

      for (let i in response) {
        let show: any = response[i];

        options.push({
          value: show.show_type + " " + show.show_name + " (" + moment(show.show_date).format("ddd, h:mm a") + ")",
          id: show._id,
          data: show
        });
      }

      setShowOptions(options);
    });
  };

  const isEmptyOptions = (options: IOption[]) => {
    if (options.length === 0) {
      return true;
    } else if (options.length === 1) {
      if (options[0].id === "None") {
        return true;
      }
    }
    return false;
  };

  const [gridApi, setGridApi] = useState<any>(null);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
  };

  const resetFilters = useCallback(() => {
    if (gridApi) {
      setShow({ id: "None", value: "All Shows" });
      setShowElement({ id: "None", value: "All" });
      setShowLevel({ id: "None", value: "All" });
      gridApi.setFilterModel(null);
    }
  }, [gridApi]);

  const [prevShowId, setPrevShowId] = useState<string | null>(null);

  useEffect(() => {
    const showIdChanged = prevShowId !== show.id;
    if (showIdChanged) {
      setPrevShowId(show.id);
      setShowElement({ id: "None", value: "All" });
      setShowLevel({ id: "None", value: "All" });

      if (show.id === "None") {
        setElementOptions([]);
        setLevelOptions([]);
        return;
      }
    }

    if (!showIdChanged) {
      return;
    }

    if (show.data !== undefined && ShowTypes[event.sanctioning_club][show.data.show_type] !== undefined) {
      let _elementOptions: IOption[] = [{ id: "None", value: "All" }];
      let _levelOptions: IOption[] = [{ id: "None", value: "All" }];

      const showSetup = ShowTypes[event.sanctioning_club][show.data.show_type];
      for (let elementName in showSetup) {
        const levels = getElementLevels(showSetup[elementName]);
        if (showSetup.length !== 1 && levels[0] !== elementName) {
          if (levels.length > 0) {
            _elementOptions.push({ value: elementName, id: elementName, data: show.data });
          } else {
            _levelOptions.push({ value: elementName, id: elementName, data: show.data });
          }
        }
      }

      setElementOptions(_elementOptions);

      if (_levelOptions.length > 1) {
        setLevelOptions(_levelOptions);
      }
    }
  }, [show.id, event.sanctioning_club, show.data, prevShowId]);

  useEffect(() => {
    if (showElement.id !== "None" && show.data !== undefined && ShowTypes[event.sanctioning_club][show.data.show_type] !== undefined) {
      setLevelOptions([]);
      setShowLevel({ id: "None", value: "All" });

      let _levelOptions: IOption[] = [{ id: "None", value: "All" }];

      const showSetup = ShowTypes[event.sanctioning_club][show.data.show_type];
      for (let elementName in showSetup) {
        if (showElement.id === elementName) {
          getElementLevels(showSetup[elementName]).forEach((level) => {
            _levelOptions.push({ value: level, id: level });
          });
        }
      }

      setLevelOptions(_levelOptions);
    } else if (showElement.id === "None" && show.data !== undefined && ShowTypes[event.sanctioning_club][show.data.show_type] !== undefined) {
      let _levelOptions: IOption[] = [{ id: "None", value: "All" }];

      const showSetup = ShowTypes[event.sanctioning_club][show.data.show_type];
      for (let elementName in showSetup) {
        const levels = getElementLevels(showSetup[elementName]);
        if (showSetup.length !== 1 && levels[0] !== elementName) {
          if (levels.length <= 0) {
            _levelOptions.push({ value: elementName, id: elementName, data: show.data });
          }
        }
      }

      setLevelOptions(_levelOptions);
    }
  }, [showElement, event.sanctioning_club, show.data]);

  useEffect(() => {
    if (!loaded) {
      getResults(event._id);
      getShows(event.club._id, event._id);
      setLoaded(true);
    }
  }, [loaded, getResults, event._id, event.club._id]);

  // Apply filters when selections change
  useEffect(() => {
    let filter: any = {};

    if (gridApi && show.id !== "None") {
      filter["show_id"] = {
        "values": [show.id],
        "filterType": "set",
      };
    }

    if (gridApi && showElement.id !== "None") {
      filter["element"] = {
        "values": [showElement.value],
        "filterType": "set"
      };
    }

    if (gridApi && showLevel.id !== "None") {
      filter["level"] = {
        "values": [showLevel.value],
        "filterType": "set"
      };
    }

    if (gridApi) {
      gridApi.setFilterModel(Object.keys(filter).length > 0 ? filter : null);
    }
  }, [gridApi, show, showElement, showLevel]);

  return (
    <div className={styles.ResultsViewer}>
      <h2 className={styles.sectionTitle}>Results</h2>
      <div className={styles.filters}>
        <div className={styles.left}>
          <Dropdown 
            className={styles.dropdown} 
            value={show} 
            onChange={(e: any, value: IOption) => { setShow(value); }} 
            label="Show" 
            options={showOptions} 
            placeholder="Select a Show" 
          />
          {show.data?.show_type !== Constants.show_type.fast_cat && (
            <Dropdown 
              className={styles.dropdown} 
              disabled={isEmptyOptions(elementOptions)} 
              value={showElement} 
              onChange={(e: any, value: IOption) => { setShowElement(value); }} 
              label="Element" 
              options={elementOptions} 
              placeholder="Select an Element" 
            />
          )}
          {show.data?.show_type !== Constants.show_type.fast_cat && (
            <Dropdown 
              className={styles.dropdown} 
              disabled={isEmptyOptions(levelOptions)} 
              value={showLevel} 
              onChange={(e: any, value: IOption) => { setShowLevel(value); }} 
              label="Level" 
              options={levelOptions} 
              placeholder="Select a Level" 
            />
          )}
          <Button className={styles.reset} onClick={() => { resetFilters(); }}>Reset</Button>
        </div>
      </div>
      <div className={styles.tableWrap}>
        <AgGridReact
          onGridReady={onGridReady}
          rowData={rowData}
          suppressRowTransform={true}
          columnDefs={colDefs}
          getRowId={(params) => params.data.scoreId}
          defaultColDef={defaultColDef}
          theme={theme}
          pagination={true}
          paginationPageSize={20}
        />
      </div>
    </div>
  );
};

export default ResultsViewer;


